import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Loader2, Download, AlertTriangle, FolderTree, Users } from "lucide-react";
import { getRegistrationDocumentTypes, downloadAllRegistrations } from "@/lib/api";

// Above this the admin is warned the download will be long.
const LARGE_BYTES = 5 * 1024 ** 3;

const fmtSize = (bytes) => {
  if (bytes >= 1024 ** 3) return `${(bytes / 1024 ** 3).toFixed(1)} GB`;
  if (bytes >= 1024 ** 2) return `${Math.round(bytes / 1024 ** 2)} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
};

const LAYOUTS = [
  {
    value: "document",
    icon: FolderTree,
    title: "By document type",
    // Illustrative sample only — placeholder names, not real students.
    example: ["10th Marksheet/", "  Rahul Sharma_10th.pdf", "  Priya Reddy_10th.pdf", "Aadhaar/", "  Rahul Sharma_Aadhaar.jpg"],
  },
  {
    value: "student",
    icon: Users,
    title: "By student",
    example: ["Rahul Sharma/", "  10th.pdf", "  Aadhaar.jpg", "Priya Reddy/", "  10th.pdf"],
  },
];

// Pick which document types to download, and whether the ZIP is organised
// per document type or per student. Uses the Registrations list's filters.
export function DocumentDownloadDialog({ open, onOpenChange, filters }) {
  const [types, setTypes] = useState([]);
  const [totalStudents, setTotalStudents] = useState(0);
  const [selected, setSelected] = useState(new Set());
  const [layout, setLayout] = useState("document");
  const [loading, setLoading] = useState(false);
  const [starting, setStarting] = useState(false);
  const filterKey = JSON.stringify(filters);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    getRegistrationDocumentTypes(filters).then((res) => {
      setLoading(false);
      if (!res.ok) {
        toast.error(res.data?.detail || "Could not load document types");
        return;
      }
      setTypes(res.data?.types || []);
      setTotalStudents(res.data?.total_students || 0);
      setSelected((prev) => new Set([...prev].filter((k) => (res.data?.types || []).some((t) => t.key === k))));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, filterKey]);

  const selectedBytes = useMemo(
    () => types.filter((t) => selected.has(t.key)).reduce((sum, t) => sum + t.bytes, 0),
    [types, selected]
  );

  const toggle = (key) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });

  const handleDownload = async () => {
    if (!selected.size) return toast.error("Select at least one document");
    setStarting(true);
    const allSelected = selected.size === types.length;
    const res = await downloadAllRegistrations(filters, {
      docKeys: allSelected ? [] : types.map((t) => t.key).filter((k) => selected.has(k)),
      layout,
    });
    setStarting(false);
    if (!res.ok) {
      toast.error(res.data?.detail || "Could not start the download — please try again");
      return;
    }
    toast.success("Download started — see your browser's downloads. Large selections can take a while.");
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="dialog-download-documents">
        <DialogHeader>
          <DialogTitle>Download documents</DialogTitle>
          <DialogDescription>
            Choose the documents to download as one ZIP, for the {totalStudents} students matching the current filters.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center justify-between text-sm">
          <span className="text-[#6B7280]">
            <span className="font-medium text-[#111]">{selected.size}</span> selected
            {selected.size > 0 && <> · about <span className="font-medium text-[#111]">{fmtSize(selectedBytes)}</span></>}
          </span>
          <div className="flex gap-3">
            <button type="button" className="text-[#B60F2A] hover:underline font-medium" onClick={() => setSelected(new Set(types.map((t) => t.key)))}>
              Select all
            </button>
            <button type="button" className="text-[#6B7280] hover:underline font-medium" onClick={() => setSelected(new Set())}>
              Clear
            </button>
          </div>
        </div>

        <div className="max-h-[38vh] overflow-y-auto border border-[#E5E7EB] rounded-lg divide-y divide-[#F3F4F6]">
          {loading ? (
            <div className="py-10 text-center text-[#9CA3AF]">
              <Loader2 className="h-5 w-5 animate-spin mx-auto mb-2" /> Loading documents…
            </div>
          ) : types.length === 0 ? (
            <div className="py-10 text-center text-[#9CA3AF]">No documents uploaded for these students.</div>
          ) : (
            types.map((t) => (
              <label key={t.key} className="flex items-center gap-3 px-4 py-2.5 text-sm cursor-pointer hover:bg-[#F7F7F7]">
                <input
                  type="checkbox"
                  checked={selected.has(t.key)}
                  onChange={() => toggle(t.key)}
                  className="h-4 w-4 accent-[#B60F2A]"
                  data-testid={`checkbox-doc-${t.key}`}
                />
                <span className="flex-1 text-[#111] font-medium">{t.label}</span>
                <span className="text-[#6B7280] tabular-nums w-28 text-right">{t.students} students</span>
                <span className="text-[#6B7280] tabular-nums w-20 text-right">{fmtSize(t.bytes)}</span>
              </label>
            ))
          )}
        </div>

        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-[#6B7280] mb-2">Organise ZIP by</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {LAYOUTS.map((l) => {
              const Icon = l.icon;
              const active = layout === l.value;
              return (
                <button
                  type="button"
                  key={l.value}
                  onClick={() => setLayout(l.value)}
                  className={`text-left rounded-lg border p-3 transition ${
                    active ? "border-[#B60F2A] bg-[#B60F2A]/5" : "border-[#E5E7EB] hover:border-[#111]"
                  }`}
                  data-testid={`layout-${l.value}`}
                >
                  <div className="flex items-center gap-2 text-sm font-semibold text-[#111]">
                    <input type="radio" readOnly checked={active} className="h-4 w-4 accent-[#B60F2A]" tabIndex={-1} />
                    <Icon className="h-4 w-4" /> {l.title}
                  </div>
                  <pre className="mt-2 text-[11px] leading-4 text-[#6B7280] font-mono whitespace-pre">{l.example.join("\n")}</pre>
                </button>
              );
            })}
          </div>
          {layout === "document" && selected.size === 1 && (
            <p className="text-xs text-[#6B7280] mt-2">One document type selected — files go straight into the ZIP, no folder.</p>
          )}
        </div>

        {selectedBytes > LARGE_BYTES && (
          <div className="flex gap-2 rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            This is a large download ({fmtSize(selectedBytes)}) and may take a long time. Consider filtering by cohort or
            picking fewer documents.
          </div>
        )}

        <DialogFooter>
          <button type="button" className="mu-btn-secondary text-sm" onClick={() => onOpenChange(false)}>
            Cancel
          </button>
          <button
            type="button"
            className="mu-btn-primary text-sm"
            disabled={starting || loading || !selected.size}
            onClick={handleDownload}
            data-testid="btn-documents-download"
          >
            {starting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            {starting ? "Starting…" : "Download ZIP"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
